import { useState, useEffect, useRef, useMemo } from "react";
import FormInput from "../../../components/controls/FormInput";
import ImageUploader from "../../../components/controls/ImageUploader";
import DateTimePicker from "../../../components/controls/DateTimePicker";
import Modal from "../../../components/controls/Modal";
import DropdownMenu from "../../../components/controls/DropdownMenu";
import StylizedEditor from "../../../components/controls/StylizedEditor";
import {
  EyeIcon,
  EyeSlashIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import Button from "../../../components/controls/Button";

const useClickOutside = (isOpen, ref, onClose) => {
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isOpen, ref, onClose]);
};

const getTagTitle = (tagId, tags) => {
  const found = tags.find((t) => String(t.id) === String(tagId));
  return found?.name || "Sélectionner un tag";
};

const getCompanyTitle = (companyId, companies) => {
  const found = companies.find((c) => String(c.id) === String(companyId));
  return found?.name || "Sélectionner une compagnie";
};

const safeParseTiptapJsonString = (value) => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (parsed && parsed.type === "doc") return parsed;
    return null;
  } catch {
    return null;
  }
};

const plainTextToTiptapJsonString = (plainText) => {
  const text = (plainText || "").trim();
  const doc = {
    type: "doc",
    content: text
      ? [
          {
            type: "paragraph",
            content: [{ type: "text", text }],
          },
        ]
      : [],
  };
  return JSON.stringify(doc);
};

const normalizeIncomingContentToTiptapJsonString = (raw) => {
  const parsed = safeParseTiptapJsonString(raw);
  if (parsed) return JSON.stringify(parsed);
  return plainTextToTiptapJsonString(raw || "");
};

export default function AdminNewsModal({
  item,
  onClose,
  onSaved,
  userId,
  isOpen = true,
}) {
  const safeItem = item || {
    id: null,
    title: "",
    text: "",
    imageUrl: "",
    publishDate: null,
    tagId: "",
    companyId: "",
    isHiddenByAdmin: false,
    isFeatured: false,
    featuredStartAt: null,
    featuredEndAt: null,
    status: "draft",
  };

  const [title, setTitle] = useState(safeItem.title);
  const [imageUrl, setImageUrl] = useState(safeItem.imageUrl);
  const [text, setText] = useState(
    normalizeIncomingContentToTiptapJsonString(
      safeItem.text || safeItem.content || ""
    )
  );
  const [tagId, setTagId] = useState(safeItem.tagId);
  const [companyId, setCompanyId] = useState(safeItem.companyId);
  const [publishDate, setPublishDate] = useState(
    safeItem.publishDate ? new Date(safeItem.publishDate) : null
  );

  const [tags, setTags] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [hiddenByAdmin, setHiddenByAdmin] = useState(safeItem.isHiddenByAdmin);
  const [isFeatured, setIsFeatured] = useState(!!safeItem.isFeatured);
  const [featuredStartAt, setFeaturedStartAt] = useState(
    safeItem.featuredStartAt ? safeItem.featuredStartAt.substring(0, 16) : ""
  );
  const [featuredEndAt, setFeaturedEndAt] = useState(
    safeItem.featuredEndAt ? safeItem.featuredEndAt.substring(0, 16) : ""
  );

  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const statusMenuRef = useRef(null);
  const isAccepted = safeItem.status === "accepted";

  useClickOutside(showStatusMenu, statusMenuRef, () =>
    setShowStatusMenu(false)
  );

  useEffect(() => {
    loadTags();
    loadCompanies();
  }, []);

  useEffect(() => {
    setTitle(safeItem.title || "");
    setImageUrl(safeItem.imageUrl || "");
    setText(
      normalizeIncomingContentToTiptapJsonString(
        safeItem.text || safeItem.content || ""
      )
    );
    setTagId(safeItem.tagId || "");
    setCompanyId(safeItem.companyId || "");
    setPublishDate(
      safeItem.publishDate ? new Date(safeItem.publishDate) : null
    );
    setHiddenByAdmin(!!safeItem.isHiddenByAdmin);
    setIsFeatured(!!safeItem.isFeatured);
    setFeaturedStartAt(
      safeItem.featuredStartAt ? safeItem.featuredStartAt.substring(0, 16) : ""
    );
    setFeaturedEndAt(
      safeItem.featuredEndAt ? safeItem.featuredEndAt.substring(0, 16) : ""
    );
    setShowStatusMenu(false);
  }, [safeItem?.id]);

  const loadCompanies = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Company/admin-list`,
      { headers: { "X-User-Id": userId } }
    );
    if (!res.ok) return;
    const data = await res.json();
    setCompanies(data.map((x) => ({ id: x.id, name: x.name })));
  };

  const loadTags = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/Tags`,
      { headers: { "X-User-Id": userId } }
    );
    if (!res.ok) return;
    const data = await res.json();
    setTags(data.map((x) => ({ id: x.id, name: x.nameFr })));
  };

  const toggleAdminHidden = async () => {
    if (!safeItem.id) return;
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/${safeItem.id}/toggle-admin-hidden`,
      { method: "POST", headers: { "X-User-Id": userId } }
    );
    if (!res.ok) return alert("Erreur lors du changement de visibilité.");
    setHiddenByAdmin((v) => !v);
  };

  const approve = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/${safeItem.id}/approve`,
      { method: "POST", headers: { "X-User-Id": userId } }
    );
    if (!res.ok) return alert("Erreur lors de l'approbation.");
    setShowStatusMenu(false);
    onSaved();
  };

  const reject = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/${safeItem.id}/reject`,
      { method: "POST", headers: { "X-User-Id": userId } }
    );
    if (!res.ok) return alert("Erreur lors du refus.");
    setShowStatusMenu(false);
    onSaved();
  };

  const isEmptyEditorJsonString = (jsonString) => {
    const parsed = safeParseTiptapJsonString(jsonString);
    if (!parsed) return !(jsonString || "").trim();
    const walk = (node) => {
      if (!node) return false;
      if (Array.isArray(node)) return node.some(walk);
      if (node.type === "text") return (node.text || "").trim().length > 0;
      if (node.content) return walk(node.content);
      return false;
    };
    return !walk(parsed);
  };

  const plainCharCount = useMemo(() => {
    const parsed = safeParseTiptapJsonString(text);
    if (!parsed) return 0;
    const walk = (node) => {
      if (!node) return "";
      if (Array.isArray(node)) return node.map(walk).join("");
      if (node.type === "text") return node.text || "";
      if (node.content) return walk(node.content);
      return "";
    };
    return walk(parsed).replace(/\s+/g, " ").trim().length;
  }, [text]);

  const save = async () => {
    if (!title.trim()) return alert("Veuillez entrer un titre.");
    if (!imageUrl.trim()) return alert("Veuillez ajouter une image.");
    if (isEmptyEditorJsonString(text))
      return alert("Veuillez entrer un texte.");
    if (!tagId) return alert("Veuillez sélectionner un tag.");
    if (!companyId) return alert("Veuillez sélectionner une compagnie.");

    const payload = {
      id: safeItem.id,
      title,
      text,
      imageUrl,
      publishDate: publishDate ? new Date(publishDate) : null,
      tagId,
      companyId: Number(companyId),
      isFeatured,
      featuredStartAt: featuredStartAt ? new Date(featuredStartAt) : null,
      featuredEndAt: featuredEndAt ? new Date(featuredEndAt) : null,
      userId,
      status: "draft",
    };

    const url = safeItem.id
      ? `${process.env.REACT_APP_API_BASE_URL}/api/News/admin-review`
      : `${process.env.REACT_APP_API_BASE_URL}/api/News`;

    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-User-Id": userId },
      body: JSON.stringify(payload),
    });

    if (!res.ok) return alert("Erreur lors de l'enregistrement.");
    onSaved();
  };

  const headerRight = (
    <div className="flex items-center gap-3">
      {safeItem.id && (
        <button
          type="button"
          onClick={toggleAdminHidden}
          className={`h-10 w-10 flex items-center justify-center rounded-full border transition ${
            hiddenByAdmin
              ? "border-red-300 bg-red-50 text-red-600 hover:bg-red-100"
              : "border-green-300 bg-green-50 text-green-600 hover:bg-green-100"
          }`}
        >
          {hiddenByAdmin ? (
            <EyeSlashIcon className="w-5 h-5" />
          ) : (
            <EyeIcon className="w-5 h-5" />
          )}
        </button>
      )}

      {safeItem.id && !isAccepted && (
        <div className="relative" ref={statusMenuRef}>
          <Button
            as="button"
            type="button"
            onClick={() => setShowStatusMenu((v) => !v)}
            size="md"
            className="w-full"
            rightIcon={<ChevronDownIcon className="w-4 h-4" />}
          >
            Changer le statut
          </Button>

          {showStatusMenu && (
            <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-lg border p-2 z-50">
              <button
                type="button"
                className="w-full text-left px-4 py-2 rounded-lg hover:bg-gray-100"
                onClick={approve}
              >
                Accepter
              </button>
              <button
                type="button"
                className="w-full text-left px-4 py-2 rounded-lg hover:bg-red-50 text-red-600"
                onClick={reject}
              >
                Refuser
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <Button
        as="button"
        type="button"
        color="secondary"
        size="md"
        onClick={onClose}
      >
        Annuler
      </Button>
      <Button
        as="button"
        type="button"
        color="primary"
        size="md"
        onClick={save}
      >
        Enregistrer
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={safeItem.id ? "Modifier l’actualité" : "Ajouter une actualité"}
      size="xl"
      footer={footer}
      showCloseButton
      headerRight={headerRight}
    >
      {safeItem.id &&
        safeItem.isFeatured &&
        safeItem.featuredStartAt &&
        safeItem.featuredEndAt && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            En vedette du {new Date(safeItem.featuredStartAt).toLocaleString()}{" "}
            au {new Date(safeItem.featuredEndAt).toLocaleString()}
          </div>
        )}

      {safeItem.id && hiddenByAdmin && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Cet article est caché par un modérateur.
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-[360px] flex-shrink-0 flex flex-col gap-4">
          <FormInput
            label="Titre"
            value={title}
            onChange={(n, v) => setTitle(v)}
            required
          />

          <DateTimePicker
            label="Date de publication"
            value={publishDate}
            onChange={setPublishDate}
          />

          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              Tag
            </label>
            <DropdownMenu
              title={getTagTitle(tagId, tags)}
              isDropdown
              withSearch
              items={tags.map((t) => ({
                key: t.id,
                label: t.name,
                onClick: () => setTagId(t.id),
              }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              Compagnie <span className="text-muted">*</span>
            </label>
            <DropdownMenu
              title={getCompanyTitle(companyId, companies)}
              isDropdown
              withSearch
              searchPlaceholder="Rechercher une compagnie..."
              items={companies.map((c) => ({
                key: c.id,
                label: c.name,
                onClick: () => setCompanyId(Number(c.id)),
              }))}
            />
          </div>

          <div className="rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={isFeatured}
                onChange={() => setIsFeatured((v) => !v)}
                className="h-5 w-5 rounded border-gray-300 checkbox-pro"
              />
              <span className="font-semibold text-primary">En vedette</span>
            </div>

            {isFeatured && (
              <div className="mt-4 flex flex-col gap-4">
                <DateTimePicker
                  label="Début"
                  value={featuredStartAt}
                  onChange={setFeaturedStartAt}
                />
                <DateTimePicker
                  label="Fin"
                  value={featuredEndAt}
                  onChange={setFeaturedEndAt}
                />
              </div>
            )}
          </div>

          <ImageUploader
            initialImageUrl={imageUrl}
            onTempImageChange={(url) => setImageUrl(url || "")}
            bucket="actualites"
            ratio="landscape"
          />
        </div>

        <div className="flex-1 min-h-[520px] flex flex-col">
          <div className="flex-1 min-h-0">
            <StylizedEditor
              label="Texte de l'article"
              valueJson={text}
              onChangeJson={setText}
              fullHeight
            >
              {" "}
            </StylizedEditor>
          </div>
        </div>
      </div>
    </Modal>
  );
}
